import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  alpha
} from '@mui/material';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { getBreakUntilMs } from '../../utils/breakSessionStorage';
import { formatSecondsToHms } from '../../utils/breakDurationHms';

/**
 * Shown when the user is marked idle (no activity beyond configured minutes). Requires punch-in
 * to acknowledge. Suppressed while a timed break session is active.
 */
export default function IdleAcknowledgmentOverlay() {
  const [status, setStatus] = useState(null);
  const [tick, setTick] = useState(0);
  const [punching, setPunching] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get('/punchcard/status');
      setStatus(r.data?.data || null);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 5000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onPunch = () => {
      fetchStatus();
      setTick((x) => x + 1);
    };
    window.addEventListener('punchStatusChanged', onPunch);
    window.addEventListener('erp4cBreakSessionStarted', onPunch);
    return () => {
      window.removeEventListener('punchStatusChanged', onPunch);
      window.removeEventListener('erp4cBreakSessionStarted', onPunch);
    };
  }, [fetchStatus]);

  const onBreak = getBreakUntilMs() != null;
  const isIdle = Boolean(status?.isIdle);
  const lastAt = status?.lastActivityAt ? new Date(status.lastActivityAt) : null;
  const idleTimeoutMinutes = status?.idleTimeoutMinutes ?? 10;

  const idleStartMs = useMemo(() => {
    if (!status?.lastActivityAt) return null;
    const t = new Date(status.lastActivityAt).getTime();
    if (Number.isNaN(t)) return null;
    return t + idleTimeoutMinutes * 60 * 1000;
  }, [status?.lastActivityAt, idleTimeoutMinutes]);

  const idleSeconds = useMemo(() => {
    if (idleStartMs == null) return 0;
    return Math.max(0, Math.floor((Date.now() - idleStartMs) / 1000));
  }, [idleStartMs, tick]);

  const shouldBlock = isIdle && !onBreak && lastAt != null;

  const handleAccept = async () => {
    setPunching(true);
    try {
      await api.post('/punchcard/punch-in', { acknowledged_idle: true });
      await fetchStatus();
      window.dispatchEvent(new CustomEvent('punchStatusChanged', { detail: { action: 'idle-ack-punch-in' } }));
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || e.message || 'Could not punch in. Try again.');
    } finally {
      setPunching(false);
    }
  };

  if (!shouldBlock && !punching) {
    return null;
  }

  return (
    <Dialog
      open={Boolean(shouldBlock || punching)}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={() => {}}
      disableEnforceFocus
      scroll="paper"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 50 }}
      BackdropProps={{ sx: { bgcolor: (t) => alpha(t.palette.error.dark, 0.35) } }}
      PaperProps={{
        'data-erp4c-idle-ack': 'true',
        sx: {
          position: 'relative',
          zIndex: 1,
          pointerEvents: 'auto',
          borderRadius: 2,
          border: (t) => `2px solid ${t.palette.error.main}`,
          boxShadow: (t) => `0 0 0 1px ${alpha(t.palette.error.main, 0.2)}`
        }
      }}
    >
      <DialogContent sx={{ pt: 3, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <WarningIcon sx={{ color: 'error.main', fontSize: 40 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'error.main' }}>
            You have been idle for more than the allowed time
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Allowed idle time in the system: <strong>{idleTimeoutMinutes} minutes</strong> without recorded activity. This
          period is tracked as <strong>idle</strong> time. To continue working, punch in and confirm below.
        </Typography>
        <Box
          sx={{
            my: 2,
            p: 2,
            borderRadius: 1,
            bgcolor: (t) => alpha(t.palette.error.main, 0.08),
            border: (t) => `1px solid ${alpha(t.palette.error.main, 0.35)}`
          }}
        >
          <Typography
            variant="h3"
            component="div"
            sx={{ fontFamily: 'monospace', fontWeight: 800, textAlign: 'center', color: 'error.main', letterSpacing: 2 }}
          >
            {formatSecondsToHms(idleSeconds)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 0.5 }}>
            Duration beyond the allowed idle threshold (HH:MM:SS, increasing)
          </Typography>
        </Box>
        {idleStartMs != null && (
          <Typography variant="body2" sx={{ textAlign: 'center', mb: 1 }}>
            <Box component="span" color="text.secondary">
              Idle from{' '}
            </Box>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {new Date(idleStartMs).toLocaleString()}
            </Box>
            <Box component="span" color="text.secondary">
              {' '}
              until now ({new Date().toLocaleString()})
            </Box>
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', position: 'relative', zIndex: 2 }}>
        <Button
          type="button"
          variant="contained"
          color="error"
          size="large"
          onClick={(e) => {
            e.stopPropagation();
            handleAccept();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          disabled={punching}
          startIcon={punching ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {punching ? 'Punching in…' : 'Accept and agree — punch in to continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
